import React, { Component } from 'react';
import CargomaticModal from 'components/v2/modal';
import { assign, findIndex, isEmpty, sortBy } from 'lodash';
import { uuid } from 'uuidv4';
import PhoneContact from 'components/v2/phone-contact';
import CargomaticCard from 'components/v2/card';
import DotsHorizontalButton from 'components/v2/buttons/DotsHorizontalButton';
import Badge from 'components/v2/Badge';
import EditButton from 'components/v2/buttons/EditButton';
import AddButton from 'components/v2/buttons/AddButton';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { getClassName } from 'utils/ui/classes';
import { showToast } from 'stores/ToastStore';
import validator from 'email-validator';
import { useTranslation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';
import { getPhoneFormatterObject } from 'utils/formatters/Phone';

const AccountContactView = (props) => {
  const { contact } = props;
  const phoneNumber = getPhoneFormatterObject(contact.phone);
  const { t } = useTranslation();

  return (
    <tr>
      <td className="">
        <div className="w-20 truncate">
          <span>{contact.name}</span>
        </div>
      </td>
      <td className="" style={{}}>
        <div className="w-32">
          {phoneNumber && <span>{phoneNumber.formatInternational()}</span>}
          {contact.phoneExtension && (
            <span className="pl-2">X{contact.phoneExtension}</span>
          )}
        </div>
      </td>
      <td className="">
        <div className="w-32 truncate">
          <span>{contact.email}</span>
        </div>
      </td>
      <td className="" style={{}}>
        <div className="w-20">
          {contact.contactType && (
            <Badge
              size="large"
              color={getContactTypeColor(contact.contactType)}
            >
              {t(sluggify(contact.contactType))}
            </Badge>
          )}
        </div>
      </td>
      <td className="text-right">
        <div className="actions">
          <DotsHorizontalButton onClick={() => props.onEditContacts()} />
        </div>
      </td>
    </tr>
  );
};

const getContactTypeColor = (type) => {
  switch (type) {
    case 'Mobile':
      return 'blue';
    case 'Finance':
      return 'yellow';
    case 'Dispatch':
      return 'purple';
    case 'Escalation':
      return 'red';
    case 'Operations':
      return 'brand';
    default:
      return 'gray';
  }
};

const AccountContactsView = (props) => {
  let { contacts, t } = props;
  const hasContacts = contacts && contacts.length > 0;
  contacts = hasContacts ? sortBy(contacts, props.sortKey) : [];

  const NAME = t('NAME');
  const TYPE = t('TYPE');
  const PHONE = titleize(t('PHONE'));
  const EMAIL = titleize(t('EMAIL'));
  return (
    <CargomaticCard
      header={() => (
        <header className="flex items-center justify-between">
          <h3 className="my-0">{titleize(t('CONTACT', { count: 2 }))}</h3>
          <div className="flex items-center space-x-4">
            {hasContacts && (
              <div className="items-center actions space-x-2">
                <label className="block text-sm form-label">
                  {t('SORT_BY')}
                </label>
                <div className="w-24">
                  <select
                    className="form-select"
                    onChange={(e) => props.onSortSelect(e.target.value)}
                  >
                    <option value="name">{NAME}</option>
                    <option value="contactType">{TYPE}</option>
                    <option value="phone">{PHONE}</option>
                    <option value="email">{EMAIL}</option>
                  </select>
                </div>
              </div>
            )}
            <EditButton onClick={() => props.onEditContactsClick()} />
          </div>
        </header>
      )}
    >
      <div className="">
        {!hasContacts && (
          <div>
            <span>{t('NO_CONTACT_AVAILABLE')}</span>
          </div>
        )}
        {hasContacts && (
          <div>
            <div className="overflow-x-auto card--no-top-padding card--no-x-padding divide-solid divide-y-2 divide-gray-200">
              <table className="default-table">
                <thead>
                  <tr>
                    <th>{NAME}</th>
                    <th style={{}}>{PHONE}</th>
                    <th style={{}}>{EMAIL}</th>
                    <th style={{}}>{TYPE}</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <AccountContactView
                      contact={c}
                      onEditContacts={() => props.onEditContactsClick()}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-sm actions text-grey-900 ">
              {hasContacts ? contacts.length : 0} {t('TOTAL')}
            </div>
          </div>
        )}
      </div>
    </CargomaticCard>
  );
};

const HeaderRow = (props) => {
  const { t } = props;
  return (
    <tr>
      <th className="">
        <span className="">{t('NAME', { count: 1 })}</span>
      </th>
      <th className="">
        <span className="w-32">{titleize(t('PHONE'))}</span>
      </th>
      <th className="">
        <span className="">{titleize(t('EXTENSION'))}</span>
      </th>
      <th className="">
        <span className="">{titleize(t('EMAIL'))}</span>
      </th>
      <th className="">
        <span className="">{titleize(t('TYPE'))}</span>
      </th>
      <th className="">
        <span className="">
          <AddButton size="small" onClick={() => props.onAdd()} />
        </span>
      </th>
    </tr>
  );
};

const ContactsEditView = (props) => {
  const { contacts } = props;
  return (
    <div className="overflow-x-auto modal--no-padding">
      <table className="default-table">
        <thead className="">
          <HeaderRow t={props.t} onAdd={props.onAdd} clone />
        </thead>
        <tbody className="">
          {contacts.map((c, i) => {
            return (
              <PhoneContact
                key={i}
                contact={c}
                handlePhoneChange={(key, value) => {
                  props.handleContactChange(c, { [key]: `${value}` });
                }}
                onRemove={() => props.onRemove(c)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

class Contacts extends Component {
  constructor(props) {
    super(props);
    const contacts =
      props.contacts && props.contacts.length > 0 ? props.contacts : [];

    this.state = {
      contacts,
      contactsEditableCopy: [...contacts],
      contactSortKey: 'name',
      showEditContactsModal: false,
    };
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { contacts } = this.state;
    if (contacts.length === 0 && newProps.contacts) {
      this.setState({
        contacts: newProps.contacts,
        contactsEditableCopy: [...newProps.contacts],
      });
    }
  }

  onEditContactsHide = () => {
    this.setState((prevState) => {
      return {
        ...prevState,
        showEditContactsModal: false,
      };
    });
  };

  onContactsSortSelect = (contactSortKey) => this.setState({ contactSortKey });

  onEditContactsClick = () => {
    const { contacts } = this.state;

    if (contacts.length === 0) {
      contacts.push(this.getNewContact());
    }

    this.setState({
      showEditContactsModal: true,
      contacts,
      contactsEditableCopy: contacts,
    });
  };

  getNewContact = () => {
    return {
      _id: uuid(),
      name: '',
      phone: '',
      email: '',
      phoneExtension: '',
      contactType: '',
    };
  };

  onAddContact = () => {
    const { contactsEditableCopy } = this.state;
    const newContact = this.getNewContact();
    this.setState((prevState) => {
      return {
        ...prevState,
        contactsEditableCopy: [...prevState.contactsEditableCopy, newContact],
      };
    });
  };

  onRemoveContact = (contact) => {
    this.setState((prevState) => {
      const index = prevState.contactsEditableCopy.findIndex(
        (c) => c._id === contact._id,
      );
      return {
        ...prevState,
        contactsEditableCopy: [
          ...prevState.contactsEditableCopy.slice(0, index),
          ...prevState.contactsEditableCopy.slice(index + 1),
        ],
      };
    });
  };

  validateContact(contact) {
    let valid = true;

    // Providing Default Values
    contact.isNameValid = true;
    contact.isEmailValid = true;
    contact.isPhoneValid = true;
    contact.isContactValid = true;
    contact.isContactTypeValid = true;

    if (isEmpty(contact.name) || contact.name.length < 3) {
      contact.isNameValid = false;
      contact.isContactValid = false;
      valid = false;
    }
    if (isEmpty(contact.contactType)) {
      contact.isContactTypeValid = false;
      contact.isContactValid = false;
      valid = false;
    }
    const phoneNumber = getPhoneFormatterObject(contact.phone);
    if (!phoneNumber || !phoneNumber.isValid()) {
      contact.isPhoneValid = false;
      contact.isContactValid = false;
      valid = false;
    }

    if (!contact.email || !validator.validate(contact.email)) {
      contact.isEmailValid = false;
      contact.isContactValid = false;
      valid = false;
    }

    return { contact, valid };
  }

  isNotEmptyContact = (c) => {
    return (
      !isEmpty(c.name) &&
      !isEmpty(c.phone) &&
      !isEmpty(c.email) &&
      !isEmpty(c.contactType)
    );
  };

  onContactsSave = async () => {
    let valid = true;
    let { contactsEditableCopy } = this.state;
    const { t } = this.props;

    contactsEditableCopy = contactsEditableCopy.map((c) => {
      const result = this.validateContact(c);
      if (result.valid) {
        c.isContactValid = true;
        c.isContactTypeValid = true;
        c.isPhoneValid = true;
        c.isEmailValid = true;
        return c;
      }
      valid = false;
      return result.contact;
    });

    if (!valid) {
      this.setState({ contactsEditableCopy });
    } else {
      try {
        await UserWebAPIUtils.updateContacts(
          this.props.userId,
          contactsEditableCopy,
        );
        showToast(t('CONTACT_UPDATED'), t('CONTACT_UPDATED'), 'success');
        this.setState((prevState) => {
          const contacts = prevState.contactsEditableCopy.filter((c) =>
            this.isNotEmptyContact(c),
          );
          return {
            ...prevState,
            contacts,
            contactsEditableCopy: contactsEditableCopy,
          };
        });
        this.onEditContactsHide();
      } catch (e) {
        showToast(t('CONTACT_UPDATE_FAILED'), e.message, 'error');
      }
    }
  };

  handleContactChange = (c, change) => {
    this.setState((prevState) => {
      const contact = { ...c, ...change };
      const contactsEditableCopy = [...prevState.contactsEditableCopy];
      const index = findIndex(contactsEditableCopy, { _id: contact._id });
      contactsEditableCopy[index] = contact;
      return { contactsEditableCopy };
    });
  };

  render() {
    const {
      contacts,
      contactsEditableCopy,
      contactSortKey,
      showEditContactsModal,
    } = this.state;
    const { t } = this.props;

    const body = (
      <ContactsEditView
        t={t}
        contacts={contactsEditableCopy}
        handleContactChange={this.handleContactChange}
        onAdd={this.onAddContact}
        onRemove={this.onRemoveContact}
      />
    );

    const footer = (
      <div className="flex justify-end">
        <button
          onClick={() => this.onEditContactsHide()}
          className="button button-secondary"
        >
          {t('CANCEL')}
        </button>
        <button
          onClick={() => this.onContactsSave()}
          className="button button-green"
        >
          {titleize(t('SAVE'))}
        </button>
      </div>
    );
    return (
      <div className={getClassName(this.props.className)}>
        <AccountContactsView
          contacts={contacts}
          onSortSelect={this.onContactsSortSelect}
          sortKey={contactSortKey}
          onEditContactsClick={this.onEditContactsClick}
          t={t}
        />
        {showEditContactsModal && (
          <CargomaticModal
            backdrop="static"
            dialogClassName="modal-override-medium"
            showCloseButton
            title={t('EDIT_CONTACTS')}
            show={showEditContactsModal}
            body={body}
            footer={footer}
            onHide={this.onEditContactsHide}
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(Contacts);
